import React, { useMemo } from 'react'
import { Box, Divider, ThemeProvider, Typography } from '@mui/material'
import { createTheme } from '@mui/material/styles'
import theme, { DEFAULT_THEME } from '~/shared/lib/theme'
import { useSteerage } from '~/entities/steerage'
import { PriorAuthorizationRelatedView } from '~/features/member-profile/care-navigation/prior-auth'
import { ReferralRequest } from './ReferralRequest'
import { ReferralDetails } from './ReferralDetails'
import { getEffectivePriorAuthorization } from './utils'

/**
 * Parent view for V3 of the SteerageView
 */
export const SteerageView: React.FC<{
  steerageId: number
  activeCaseId: number | null
}> = props => {
  const { data: steerage, isPending: steerageIsLoading } = useSteerage(props.steerageId)
  const showReferralDetails = steerage?.segment !== null

  /**
   * Powering read-only views
   * Referral request is disabled based on locked status *and* segmentation
   * Referral details is based only on locked status
   */
  const readOnlyRequest =
    Boolean(steerage?.is_locked) ||
    Boolean(steerage?.was_elation_letter_created_from_lucian) ||
    steerage?.segment !== null
  const readOnlyDetails = !!steerage?.is_locked

  const effectivePriorAuthorization = useMemo(
    () => (steerage ? getEffectivePriorAuthorization(steerage) : null),
    [steerage],
  )

  return (
    <>
      {!steerageIsLoading && steerage && (
        <Box>
          <Divider sx={{ borderColor: t => t.palette.grey['400'] }} />
          <Typography align="center" variant="subtitle2" sx={{ my: 1 }}>
            Status: {steerage.status}
          </Typography>
          <Divider sx={{ borderColor: t => t.palette.grey['400'] }} />
          {effectivePriorAuthorization ? (
            <Box key={`prior_auth_${effectivePriorAuthorization.pk}`} sx={{ mt: 4, ml: 1 }}>
              <PriorAuthorizationRelatedView model={effectivePriorAuthorization} />
            </Box>
          ) : null}
          <ReadOnlyWrapper enabled={readOnlyRequest}>
            <ReferralRequest steerage={steerage} />
          </ReadOnlyWrapper>
          {showReferralDetails ? (
            <ReadOnlyWrapper enabled={readOnlyDetails}>
              <ReferralDetails
                steerageId={steerage.id}
                personId={steerage?.person ?? null}
                activeCaseId={props.activeCaseId}
              />
            </ReadOnlyWrapper>
          ) : null}
        </Box>
      )}
    </>
  )
}

/**
 * This theme is used to override the default MUI theme for the SteerageView.
 * This powers the read-only view so that we can achieve the look we're after
 * instead of duplicating a lot of code to render inputs as Typography components.
 */
const steerageTheme = createTheme({
  ...theme,
  components: {
    ...theme.components,
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: DEFAULT_THEME.palette.grey[500],
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          '&.Mui-disabled': {
            color: DEFAULT_THEME.palette.grey[700],
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: DEFAULT_THEME.palette.text.primary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: DEFAULT_THEME.palette.grey[700],
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          '&.Mui-disabled': {
            '-webkit-text-fill-color': 'inherit!important',
            color: DEFAULT_THEME.palette.text.primary,
          },
        },
        root: {
          '&.Mui-disabled': {
            '&:before': {
              border: 0,
            },
          },
        },
      },
    },
  },
})

const ReadOnlyWrapper: React.FC<React.PropsWithChildren<{ enabled: boolean }>> = props => {
  if (props.enabled) return <ThemeProvider theme={steerageTheme}>{props.children}</ThemeProvider>
  return <>{props.children}</>
}
